import { toast } from '@heroui/react'
import { create } from 'zustand'

import { warehouseApi } from '@/api/warehouse.api'
import i18n from '@/i18n'
import type {
  CreateWarehouseArticleRequest,
  UpdateWarehouseArticleRequest,
  WarehouseHistoryEntry,
  WarehouseImportResult,
} from '@/types/warehouse'
import { getAppSettings, updateAppSettings } from '@/utils/settings'

export interface UiWarehouseState {
  name: string;
  count: number;
  cost: number;
}

export interface UiWarehouseArticle {
  id: string;
  date: string;
  name: string;
  description?: string;
  parsedName: string;
  isInstall: boolean;
  vehiclePlate: string | null;
  count: number;
  cost: number;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleReportItem {
  id: string;
  name: string;
  count: number;
  date: string;
  totalCost: number;
  vehicleId: string;
}

interface WarehouseStore {
  // Data
  state: UiWarehouseState[];
  history: UiWarehouseArticle[];
  vehicleReport: VehicleReportItem[];

  // Report selection persistence
  reportVehicleId: string;
  reportVehiclePlate: string | null;

  // UI Loading/Error states
  isLoading: boolean;
  isReportLoading: boolean;
  error: string | null;

  // Filters for History list
  historyFilters: {
    name: string;
  };

  // Pagination for Vehicle Report list
  reportPagination: {
    page: number;
    limit: number;
    totalPages: number;
    total: number;
  };

  // Actions
  fetchState: () => Promise<void>;
  fetchHistory: () => Promise<void>;
  fetchVehicleReport: (vehiclePlate: string) => Promise<void>;
  setReportVehicle: (id: string, plate: string | null) => void;
  importArticles: (formData: FormData, forceUpload?: boolean) => Promise<WarehouseImportResult>;
  addArticle: (data: {
    name: string;
    description?: string;
    date: string;
    count: number;
    cost: number;
  }) => Promise<void>;
  addInstallation: (data: {
    name: string;
    vehicleId: string;
    date: string;
    count: number;
  }) => Promise<void>;
  updateArticle: (
    id: string,
    patch: {
      name: string;
      description?: string;
      date: string;
      count: number;
      cost: number;
    }
  ) => Promise<void>;
  updateInstallation: (
    id: string,
    data: {
      name: string;
      vehicleId: string;
      date: string;
      count: number;
    }
  ) => Promise<void>;
  deleteArticle: (id: string) => Promise<void>;

  // UI Actions
  setHistoryFilters: (filters: Partial<WarehouseStore['historyFilters']>) => void;
  resetHistoryFilters: () => void;

  setReportPagination: (pagination: Partial<WarehouseStore['reportPagination']>) => void;

  clearError: () => void;
}

const mapHistoryEntryToUi = (e: WarehouseHistoryEntry): UiWarehouseArticle => {
  return {
    id: e.ID,
    date: e.Date,
    name: e.ArticleName,
    parsedName: e.ArticleName,
    isInstall: e.Type === 'installation',
    vehiclePlate: e.VehicleNumber || null,
    count: Math.abs(e.Count),
    cost: e.Cost,
    createdAt: e.CreatedAt,
    updatedAt: e.CreatedAt,
  }
}

export async function getVehicleReportFromInstallations(
  vehiclePlate: string
): Promise<VehicleReportItem[]> {
  // Fetch installations for the vehicle
  const installationsResponse = await warehouseApi.getWarehouseInstallations({
    vehicle_number: vehiclePlate,
    limit: 10000,
  })

  // Fetch additions to resolve names and costs
  const articlesResponse = await warehouseApi.getWarehouseArticles({ limit: 10000 })

  // Map additions by ID for O(1) lookup
  const additionsMap: Record<string, { name: string; cost: number }> = {}
  articlesResponse.Articles.forEach(a => {
    additionsMap[a.ID] = {
      name: a.Name,
      cost: a.Cost,
    }
  })

  // Map installations
  const reportItems: VehicleReportItem[] = []
  installationsResponse.Installations.forEach(inst => {
    const addition = additionsMap[inst.WarehouseArticleID]
    if (addition) {
      reportItems.push({
        id: inst.ID,
        name: addition.name,
        count: inst.Count,
        date: inst.Date,
        totalCost: inst.Count * addition.cost,
        vehicleId: inst.VehicleID,
      })
    }
  })

  // Sort by date desc
  reportItems.sort((a, b) => b.date.localeCompare(a.date))

  return reportItems
}

export const useWarehouseStore = create<WarehouseStore>((set, get) => ({
  state: [],
  history: [],
  vehicleReport: [],
  reportVehicleId: 'none',
  reportVehiclePlate: null,
  isLoading: false,
  isReportLoading: false,
  error: null,
  historyFilters: {
    name: '',
  },
  reportPagination: {
    page: 0,
    limit: getAppSettings().warehouseLimit ?? 10,
    totalPages: 0,
    total: 0,
  },

  fetchState: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await warehouseApi.getWarehouseState()
      const stateList: UiWarehouseState[] = response.State.map(s => ({
        name: s.Name,
        count: s.Count,
        cost: s.Cost,
      }))
      stateList.sort((a, b) => a.name.localeCompare(b.name))
      set({ state: stateList })
    } catch (err) {
      const message = (err as Error).message
      set({ error: message })
      toast.danger(i18n.t('warehouse.toast.fetch_state_error'), { description: message })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchHistory: async () => {
    const { historyFilters } = get()
    set({ isLoading: true, error: null })

    try {
      const [historyResponse, articlesResponse] = await Promise.all([
        warehouseApi.getWarehouseHistory(),
        warehouseApi.getWarehouseArticles({ limit: 10000 }).catch(() => null),
      ])

      const descriptionsMap = new Map<string, string>()
      if (articlesResponse?.Articles) {
        articlesResponse.Articles.forEach(a => {
          if (a.Description) {
            descriptionsMap.set(a.ID, a.Description)
          }
        })
      }

      let allMapped = historyResponse.Entries.map(e => {
        const item = mapHistoryEntryToUi(e)
        if (descriptionsMap.has(e.ID)) {
          item.description = descriptionsMap.get(e.ID)
        }
        return item
      })

      // Filter by name or description locally
      if (historyFilters.name) {
        const search = historyFilters.name.toLowerCase()
        allMapped = allMapped.filter(a =>
          a.parsedName.toLowerCase().includes(search) ||
          (a.description && a.description.toLowerCase().includes(search))
        )
      }

      // Sort chronologically (newest first)
      allMapped.sort((a, b) => {
        if (a.date !== b.date) return b.date.localeCompare(a.date)
        return b.createdAt.localeCompare(a.createdAt)
      })

      set({ history: allMapped })
    } catch (err) {
      const message = (err as Error).message
      set({ error: message })
      toast.danger(i18n.t('warehouse.toast.fetch_articles_error'), { description: message })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchVehicleReport: async (vehiclePlate: string) => {
    const { reportPagination } = get()
    set({ isReportLoading: true })
    try {
      const allMapped = await getVehicleReportFromInstallations(vehiclePlate)

      // Paginate locally
      const total = allMapped.length
      const totalPages = Math.ceil(total / reportPagination.limit)
      const pageIndex = Math.min(reportPagination.page, Math.max(0, totalPages - 1))
      const start = pageIndex * reportPagination.limit
      const pageItems = allMapped.slice(start, start + reportPagination.limit)

      set({
        vehicleReport: pageItems,
        reportPagination: {
          ...reportPagination,
          page: pageIndex,
          totalPages,
          total,
        },
      })
    } catch (err) {
      console.error("Failed to load vehicle report", err)
    } finally {
      set({ isReportLoading: false })
    }
  },

  setReportVehicle: (id, plate) => {
    set(state => ({
      reportVehicleId: id,
      reportVehiclePlate: plate,
      reportPagination: { ...state.reportPagination, page: 0 }
    }))
    if (plate) {
      get().fetchVehicleReport(plate)
    } else {
      set({ vehicleReport: [] })
    }
  },

  importArticles: async (formData, forceUpload) => {
    set({ isLoading: true, error: null })
    try {
      const result = await warehouseApi.importWarehouseArticles(formData, forceUpload)

      toast.success(i18n.t('warehouse.toast.import_success', 'Импорт успешно завершен'), {
        description: i18n.t('warehouse.toast.import_success_desc', {
          total: result.Total,
          skipped: result.Skipped,
          defaultValue: `Добавлено: ${result.Total}, пропущено: ${result.Skipped}`,
        }),
      })

      await get().fetchHistory()
      await get().fetchState()
      const currentPlate = get().reportVehiclePlate
      if (currentPlate) {
        await get().fetchVehicleReport(currentPlate)
      }
      return result
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string }
      const message = apiErr.message || (err as Error).message
      if (apiErr.status !== 409) {
        toast.danger(i18n.t('warehouse.toast.import_error', 'Не удалось импортировать данные'), { description: message })
      }
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  addArticle: async data => {
    set({ isLoading: true, error: null })
    try {
      const req: CreateWarehouseArticleRequest = {
        Name: data.name,
        Description: data.description,
        Date: data.date,
        Count: data.count,
        Cost: data.cost,
      }
      await warehouseApi.createWarehouseArticle(req)

      toast.success(i18n.t('warehouse.toast.create_success'), {
        description: i18n.t('warehouse.toast.create_success_desc'),
      })

      await get().fetchHistory()
      await get().fetchState()
    } catch (err) {
      const message = (err as Error).message
      toast.danger(i18n.t('warehouse.toast.create_error'), { description: message })
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  addInstallation: async data => {
    set({ isLoading: true, error: null })
    try {
      // 1. Try to find the lot ID from history in memory first
      let lot = get().history.find(h => !h.isInstall && h.name.toLowerCase() === data.name.toLowerCase())

      if (!lot) {
        // Fallback: Find additions matching the part name
        const response = await warehouseApi.getWarehouseArticles({ name: data.name })
        const additions = response.Articles.filter(a => a.Name.toLowerCase() === data.name.toLowerCase())
        if (additions.length === 0) {
          throw new Error(`Деталь "${data.name}" не найдена на складе`)
        }
        lot = additions[0] as any
      }

      const lotId = lot ? (lot.id || (lot as any).ID) : null

      await warehouseApi.createWarehouseInstallation({
        WarehouseArticleID: lotId,
        VehicleID: data.vehicleId,
        Date: data.date,
        Count: data.count,
      })

      toast.success(i18n.t('warehouse.toast.create_success'), {
        description: i18n.t('warehouse.toast.create_success_desc'),
      })

      await get().fetchHistory()
      await get().fetchState()
      const currentPlate = get().reportVehiclePlate
      if (currentPlate) {
        await get().fetchVehicleReport(currentPlate)
      }
    } catch (err) {
      const message = (err as Error).message
      toast.danger(i18n.t('warehouse.toast.create_error'), { description: message })
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updateArticle: async (id, patch) => {
    set({ isLoading: true, error: null })
    try {
      const req: UpdateWarehouseArticleRequest = {
        Name: patch.name,
        Description: patch.description,
        Date: patch.date,
        Count: patch.count,
        Cost: patch.cost,
      }
      await warehouseApi.updateWarehouseArticle(id, req)

      toast.success(i18n.t('warehouse.toast.update_success'), {
        description: i18n.t('warehouse.toast.update_success_desc'),
      })

      await get().fetchHistory()
      await get().fetchState()
    } catch (err) {
      const message = (err as Error).message
      toast.danger(i18n.t('warehouse.toast.update_error'), { description: message })
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  updateInstallation: async (id, data) => {
    set({ isLoading: true, error: null })
    try {
      // 1. Delete the old installation
      await warehouseApi.deleteWarehouseInstallation(id)

      // 2. Resolve target addition lot ID
      let lot = get().history.find(h => !h.isInstall && h.name.toLowerCase() === data.name.toLowerCase())

      if (!lot) {
        const response = await warehouseApi.getWarehouseArticles({ name: data.name })
        const additions = response.Articles.filter(a => a.Name.toLowerCase() === data.name.toLowerCase())
        if (additions.length === 0) {
          throw new Error(`Деталь "${data.name}" не найдена на складе`)
        }
        lot = additions[0] as any
      }

      const lotId = lot ? (lot.id || (lot as any).ID) : null

      await warehouseApi.createWarehouseInstallation({
        WarehouseArticleID: lotId,
        VehicleID: data.vehicleId,
        Date: data.date,
        Count: data.count,
      })

      toast.success(i18n.t('warehouse.toast.update_success'), {
        description: i18n.t('warehouse.toast.update_success_desc'),
      })

      await get().fetchHistory()
      await get().fetchState()
      const currentPlate = get().reportVehiclePlate
      if (currentPlate) {
        await get().fetchVehicleReport(currentPlate)
      }
    } catch (err) {
      const message = (err as Error).message
      toast.danger(i18n.t('warehouse.toast.update_error'), { description: message })
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  deleteArticle: async id => {
    set({ isLoading: true, error: null })
    try {
      const found = get().history.find(h => h.id === id) || get().vehicleReport.find(r => r.id === id)
      // Check if it is an installation
      const isInstall = found ? ('isInstall' in found ? found.isInstall : true) : false
      if (isInstall || (found && !('isInstall' in found))) {
        await warehouseApi.deleteWarehouseInstallation(id)
      } else {
        await warehouseApi.deleteWarehouseArticle(id)
      }

      toast.success(i18n.t('warehouse.toast.delete_success'), {
        description: i18n.t('warehouse.toast.delete_success_desc'),
      })

      await get().fetchHistory()
      await get().fetchState()
      const currentPlate = get().reportVehiclePlate
      if (currentPlate) {
        await get().fetchVehicleReport(currentPlate)
      }
    } catch (err) {
      const message = (err as Error).message
      toast.danger(i18n.t('warehouse.toast.delete_error'), { description: message })
      set({ error: message })
      throw err
    } finally {
      set({ isLoading: false })
    }
  },

  setHistoryFilters: newFilters => {
    set(state => ({
      historyFilters: { ...state.historyFilters, ...newFilters },
    }))
    get().fetchHistory()
  },

  resetHistoryFilters: () => {
    set({
      historyFilters: {
        name: '',
      },
    })
    get().fetchHistory()
  },

  setReportPagination: newPagination => {
    set(state => {
      const updatedPagination = { ...state.reportPagination, ...newPagination }
      if (newPagination.limit !== undefined) {
        updateAppSettings({ warehouseLimit: newPagination.limit })
      }
      return { reportPagination: updatedPagination }
    })
    const currentPlate = get().reportVehiclePlate
    if (currentPlate) {
      get().fetchVehicleReport(currentPlate)
    }
  },

  clearError: () => set({ error: null }),
}))
